import { inject } from '@angular/core';
import { HttpErrorResponse } from '@angular/common/http';
import {
  CanActivateFn,
  RedirectCommand,
  Router,
  UrlTree,
} from '@angular/router';
import { Observable, catchError, finalize, map, of, switchMap } from 'rxjs';
import { MojitoAccessApiService } from '../services/mojito-access-api.service';
import { UserService } from '../services/user.service';
import { GlobalLoadingService } from '../services/global-loading.service';
import { PostLoginRedirectService } from '../services/post-login-redirect.service';
import { MojitoAccessErrorBody } from '../../shared/models/mojito-access.model';

/** Clave del `history.state` con la que el selector recibe el nombre de la entidad que falta. */
export const MOJITO_COMPANY_NAME_STATE_KEY = 'mojitoCompanyName';

/**
 * Entrada desde «Global Analytics» de Mojito360: `/:lang/from-mojito/:mojitoCompanyId`.
 *
 * Es un guard que siempre redirige, como `authCallbackGuard`, porque no hay nada
 * que pintar: pide acceso al backend y lleva al usuario a donde toca.
 * - 200: a la página de inicio de la entidad que devuelve (casos A, B y S),
 *   recargando antes `/me` si la entidad aún no estaba en su lista, porque si no
 *   `companyGuard` lo devolvería al selector.
 * - 404: al selector con `?missing=` y, en el `state`, el nombre de la entidad
 *   de Mojito360 que todavía no está dada de alta (casos C y C2).
 * - 403 y cualquier otro error: al selector de siempre (caso D y fallos).
 */
export const mojitoAccessGuard: CanActivateFn = (route) => {
  const api = inject(MojitoAccessApiService);
  const userService = inject(UserService);
  const loading = inject(GlobalLoadingService);
  const redirect = inject(PostLoginRedirectService);
  const router = inject(Router);

  const lang = route.parent?.paramMap.get('lang') ?? 'es';
  const selectCompany = router.createUrlTree(['/', lang, 'select-company']);

  // La entrada es de un solo uso: si se quedara recordada, un nuevo login en
  // esta pestaña volvería a pedir acceso en vez de llevar a donde estaba.
  redirect.consume();

  const mojitoCompanyId = Number(route.paramMap.get('mojitoCompanyId'));
  if (!Number.isInteger(mojitoCompanyId) || mojitoCompanyId <= 0) {
    return selectCompany;
  }

  loading.show();

  return api.requestAccess(mojitoCompanyId).pipe(
    switchMap((response): Observable<UrlTree> => {
      const companyCode = response.companyCode;
      if (!companyCode) {
        return of(selectCompany);
      }

      const home = router.createUrlTree(['/', lang, companyCode, 'home']);
      const mustReloadMe =
        response.roleAssigned === true || !userService.findCompanyByCode(companyCode);

      return mustReloadMe ? userService.fetchMe().pipe(map(() => home)) : of(home);
    }),
    catchError((error: unknown) => {
      if (error instanceof HttpErrorResponse && error.status === 404) {
        return of(notProvisioned(router, lang, mojitoCompanyId, error));
      }

      return of(selectCompany);
    }),
    finalize(() => loading.hide()),
  );
};

/** El selector con el aviso de entidad no dada de alta, con su nombre si el backend lo envía. */
function notProvisioned(
  router: Router,
  lang: string,
  mojitoCompanyId: number,
  error: HttpErrorResponse,
): RedirectCommand {
  const target = router.createUrlTree(['/', lang, 'select-company'], {
    queryParams: { missing: mojitoCompanyId },
  });
  const body: unknown = error.error;
  const rawName =
    typeof body === 'object' && body !== null
      ? (body as MojitoAccessErrorBody).mojitoCompanyName
      : undefined;
  const name = typeof rawName === 'string' ? rawName.trim() : '';

  return new RedirectCommand(target, {
    state: name ? { [MOJITO_COMPANY_NAME_STATE_KEY]: name } : {},
  });
}
